import React, { useState, FC } from 'react'
import { Trans, t } from '@lingui/macro'
import { useQuery } from '@apollo/client'
import { filter, chain } from 'lodash'

import { Page } from 'front-end/src/components/common/page/Page'

import { Box, ButtonPrimary } from 'front-end/src/components'
import { ITagCompleteGQL } from '../../../types/Tag'
import Message, { MessageTypes } from '../../components/common/message/Message'
import PageHeader from '../../components/common/page/header/PageHeader'
import Spinner from '../../components/common/loader/Loader'
import Select, { SelectVariant } from '../../components/common/select/Select'
import { CreateSurveyModal } from './CreateSurveyModal'
import SurveysList from './SurveysList'
import { ISurvey } from './types'
import HeadTitle from '../../components/common/head/HeadTitle'
import { SurveysDocument, useSurveysListTagsQuery } from './Surveys.codegen'

import './Surveys.less'
import { IconPlus } from 'front-end/src/components/common/icon/IconPlus'

export const anyOption = { value: null, label: <Trans>Any</Trans> }

interface IResponse {
  admin: {
    surveys: ISurvey[]
  }
}

type ValueAndLabel = {
  value: string | null
  label: string
}

const getTagOptions = (
  tags: (ITagCompleteGQL & { surveysCount: number })[]
): ValueAndLabel[] => {
  return (
    chain(tags || [])
      .orderBy(({ name }) => name)
      .map(({ id, name }) => ({ value: String(id), label: name }))
      // @ts-expect-error
      .unshift(anyOption)
      .value()
  )
}

export const SurveysPage: FC = () => {
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false)
  const [filterTagId, setFilterTagId] = useState<number | null>(null)
  const { data: dataTags, loading: loadingTags } = useSurveysListTagsQuery()

  const { data, loading, error } = useQuery<IResponse>(SurveysDocument, {
    variables: {
      tagId: filterTagId
    },
    fetchPolicy: 'cache-first'
  })

  if (loading || loadingTags) {
    return <Spinner />
  }

  if (error) {
    return (
      <Message type={MessageTypes.ERROR}>
        <Trans>There was an error while loading surveys.</Trans>
      </Message>
    )
  }

  let tags: ValueAndLabel[] = []
  if (dataTags && dataTags.tags) {
    const filteredTags = filter(
      dataTags.tags,
      ({ surveysCount }) => surveysCount > 0
    )
    tags = getTagOptions(filteredTags)
  }

  return (
    <Page className="Survey">
      <HeadTitle title={t`Surveys`} />
      <PageHeader
        title={t`Surveys`}
        className="PageHeader"
        showBackButton={false}
        controls={
          <ButtonPrimary
            mr="10px"
            onClick={() => {
              setShowCreateModal(true)
            }}
            leftIcon={<IconPlus />}
          >
            <Trans>New survey</Trans>
          </ButtonPrimary>
        }
      >
        {tags && (
          <Box className="FilterSelect">
            <Select
              label={<Trans>Tag</Trans>}
              labelClassName="FilterSelect__label"
              options={tags}
              variant={SelectVariant.WHITE}
              onChange={(option) => setFilterTagId(option?.value as number)}
              value={tags.find((tag) => tag.value === filterTagId)}
              className="FilterSelect__Select"
            />
          </Box>
        )}
      </PageHeader>

      <div className="Survey__list">
        {data && <SurveysList surveys={data.admin.surveys || []} />}
      </div>
      {showCreateModal && (
        <CreateSurveyModal
          onHide={() => {
            setShowCreateModal(false)
          }}
        />
      )}
    </Page>
  )
}
